import { Pencil, Trash2, ChevronUp, ChevronDown, ExternalLink, GripVertical } from 'lucide-react'
import type { Link } from '../types'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface LinkCardProps {
  id: number
  link: Link
  onEdit: () => void
  onDelete: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  hasValidationError?: boolean
}

function getBadgeStyle(badge?: string): string {
  switch (badge) {
    case 'official':
      return 'bg-rhs-gold text-rhs-maroon'
    case 'video':
      return 'bg-rhs-maroon text-white'
    case 'pdf':
      return 'bg-coolgray text-rhs-black'
    case 'internal':
      return 'bg-ice-dark text-gray-600'
    default:
      return ''
  }
}

function getBadgeIcon(badge?: string): string {
  switch (badge) {
    case 'official':
      return '⭐'
    case 'video':
      return '🎥'
    case 'pdf':
      return '📄'
    case 'internal':
      return '🔒'
    default:
      return ''
  }
}

export function LinkCard({
  id,
  link,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  hasValidationError,
}: LinkCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg border-l-4 p-3 transition-shadow hover:shadow-lg ${
        hasValidationError ? 'border-red-500' : 'border-rhs-gold'
      } ${isDragging ? 'shadow-gold-lg z-10' : ''}`}
    >
      <div className="flex items-start gap-3">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-coolgray-dark hover:text-rhs-maroon transition-colors flex-shrink-0"
          aria-label="Drag to reorder"
        >
          <GripVertical size={20} />
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-title font-bold text-rhs-maroon truncate">
              {link.label || '(No label)'}
            </h3>
            {link.badge && (
              <span
                className={`px-2 py-0.5 text-xs font-title font-bold rounded-full uppercase tracking-wide flex-shrink-0 ${getBadgeStyle(
                  link.badge
                )}`}
              >
                <span className="mr-1">{getBadgeIcon(link.badge)}</span>
                {link.badge}
              </span>
            )}
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-rhs-gold hover:text-rhs-bronze flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={14} />
            </a>
          </div>
          {link.desc && <p className="text-xs text-gray-600 line-clamp-1 mb-1">{link.desc}</p>}
          <p className="text-xs text-gray-600 truncate opacity-70">{link.href}</p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1 flex-shrink-0">
          <div className="flex gap-1">
            <button
              onClick={onEdit}
              className="p-1.5 text-rhs-maroon hover:bg-ice rounded transition-colors"
              aria-label="Edit"
            >
              <Pencil size={16} />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
              aria-label="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
          <div className="flex gap-1">
            {onMoveUp && (
              <button
                onClick={onMoveUp}
                className="p-1.5 text-gray-600 hover:bg-ice rounded transition-colors"
                aria-label="Move up"
              >
                <ChevronUp size={16} />
              </button>
            )}
            {onMoveDown && (
              <button
                onClick={onMoveDown}
                className="p-1.5 text-gray-600 hover:bg-ice rounded transition-colors"
                aria-label="Move down"
              >
                <ChevronDown size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
      {hasValidationError && (
        <p className="text-xs text-red-600 mt-2">This link has validation errors</p>
      )}
    </div>
  )
}
